/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Paper
} from '@mui/material';
import { toast } from 'react-toastify';
import { toDayNumber, priceOn, formatRate } from '../../utils/ratePricing';

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Effective dates are stored at UTC noon; show their literal calendar day.
const formatDay = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const personName = (p) => (p && typeof p === 'object' ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email : '');

/**
 * Status of each price entry relative to today: the one in effect is
 * "Current", later ones "Upcoming", earlier ones "Past".
 */
export const describeSchedule = (rate) => {
  const today = toDayNumber(new Date());
  const entries = (rate?.price_schedule || []).map(e => ({
    ...e,
    from: e.effective_from ? toDayNumber(e.effective_from) : -Infinity
  }));
  const active = entries.filter(e => !e.cancelled_at).sort((a, b) => a.from - b.from);
  const current = [...active].reverse().find(e => e.from <= today) || null;
  return {
    entries: entries.sort((a, b) => a.from - b.from || new Date(a.created_at) - new Date(b.created_at)).map(e => ({
      ...e,
      status: e.cancelled_at ? 'Cancelled' : e === current ? 'Current' : e.from > today ? 'Upcoming' : 'Past'
    })),
    upcoming: active.filter(e => e.from > today)
  };
};

const STATUS_COLORS = { Current: 'success', Upcoming: 'primary', Past: 'default', Cancelled: 'default' };

const RatePriceSchedule = ({ open, unit, rate, onClose, onUpdated }) => {
  const [price, setPrice] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const { entries } = useMemo(() => describeSchedule(rate), [rate]);
  const basis = rate?.rate_basis || 'day';
  const isPast = effectiveFrom && effectiveFrom < todayInput();

  const reset = () => { setPrice(''); setEffectiveFrom(''); setNote(''); };

  const handleAdd = async () => {
    const value = parseFloat(price);
    if (isNaN(value) || value < 0) {
      toast.error('Enter a price of 0 or more');
      return;
    }
    if (!effectiveFrom) {
      toast.error('Choose the date the price takes effect');
      return;
    }
    if (isPast && !window.confirm(
      `${formatDay(effectiveFrom)} is in the past. Usage from that date that hasn't been invoiced yet will be priced at ${formatRate(value)}. ` +
      'Invoices already created keep the prices they were billed at. Continue?'
    )) return;

    setSaving(true);
    try {
      const res = await axios.post(`/api/units/${unit._id}/rates/${rate.rate_id}/prices`, {
        rate: value,
        effective_from: effectiveFrom,
        note
      });
      toast.success(`${formatRate(value)} scheduled from ${formatDay(effectiveFrom)}`);
      reset();
      onUpdated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to schedule the price');
    }
    setSaving(false);
  };

  const handleCancel = async (entry) => {
    const reason = window.prompt(
      `Cancel the ${formatRate(entry.rate)} price from ${formatDay(entry.effective_from)}? ` +
      'It stays in the history, marked cancelled.\n\nReason (optional):'
    );
    if (reason === null) return;
    try {
      const res = await axios.post(`/api/units/${unit._id}/rates/${rate.rate_id}/prices/${entry._id}/cancel`, { reason });
      toast.success('Price cancelled');
      onUpdated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel the price');
    }
  };

  if (!rate) return null;

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>
        Price Schedule: {rate.display_name || rate.rate_id}
        <Typography variant="body2" color="text.secondary">
          {unit?.base_unit} · current price {formatRate(priceOn(rate))} / {basis}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each price applies from its effective date until the next one. Invoices bill every day of
          compartment usage at the price in effect on that day, and resources at the price in effect on
          the invoice's billing end date. Invoices already created keep the prices they were billed at.
        </Typography>

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Effective From</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Recorded</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(entry => {
              const cancelled = entry.status === 'Cancelled';
              return (
                <TableRow key={entry._id} sx={cancelled ? { '& td': { color: 'text.disabled' } } : undefined}>
                  <TableCell sx={cancelled ? { textDecoration: 'line-through' } : undefined}>
                    {entry.effective_from ? formatDay(entry.effective_from) : 'Opening price'}
                  </TableCell>
                  <TableCell align="right" sx={cancelled ? { textDecoration: 'line-through' } : undefined}>
                    {formatRate(entry.rate)} / {basis}
                  </TableCell>
                  <TableCell>
                    <Chip label={entry.status} size="small" color={STATUS_COLORS[entry.status]} variant={entry.status === 'Current' ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell>
                    {entry.note}
                    {cancelled && (
                      <Typography variant="caption" display="block">
                        Cancelled {new Date(entry.cancelled_at).toLocaleDateString()}
                        {personName(entry.cancelled_by) && ` by ${personName(entry.cancelled_by)}`}
                        {entry.cancel_reason && `: ${entry.cancel_reason}`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                      {personName(entry.created_by) && <><br />{personName(entry.created_by)}</>}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {!cancelled && entry.effective_from && (
                      <Button size="small" color="error" onClick={() => handleCancel(entry)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Schedule a price change</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <TextField
              label={`New price ($ / ${basis})`}
              type="number"
              size="small"
              inputProps={{ min: 0, step: 'any' }}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              sx={{ width: 180 }}
            />
            <TextField
              label="Effective from"
              type="date"
              size="small"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <TextField
              label="Note (optional)"
              size="small"
              placeholder="e.g. FY2028 rate increase"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button variant="contained" onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving...' : 'Schedule'}
            </Button>
          </Box>
          {isPast && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This date is in the past. Usage from {formatDay(effectiveFrom)} that hasn't been invoiced yet
              will be priced at the new price.
            </Alert>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RatePriceSchedule;
